import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from '@angular/core';
import { IAssociativeMap, IGraphEdge } from 'buzzyears.core';
import {
  IWorkflow,
  IWorkflowEdge,
  Workflow,
  WorkflowDependencyProvider,
  WorkflowEdge,
  WorkflowFactory,
  WorkflowVertex,
  WorkflowVertexFactory,
  WorkflowVertexLibrary,
  WorkflowVertexSpecification,
  WorkflowVisibilityType
} from 'component.workflow.core';

import { AbstractWorkflowDesignerDispatcherObserver } from './abstract-workflow-designer-dispatcher-observer';
import {
  WorkflowDesignerDispatcherAction,
  WorkflowDesignerDispatcherActionType as ActionType,
  WorkflowDesignerDispatcherService
} from './workflow-designer-dispatcher.service';

import { WorkflowGraphComponent } from '../graph';

enum ContextView {
  Library = 'library',
  Test = 'test'
}

@Component({
  selector: 'wf-designer',
  template: `

    <ui-designer [fullscreen]="maximized" #designer>
      <ui-designer-v-box>
        <ui-designer-header>

          <nav class="d-flex flex-row align-items-center justify-content-between">

            <div class="d-flex align-items-center">
              <span class="icon cluster-icon"></span>
              <strong style="text-transform: uppercase">{{ workflow.name }}</strong>
              <span>&nbsp; ({{visibilityTypeText}})</span>
            </div>

            <div class="d-flex align-items-center">

              <button class="btn icon panel-left-icon" [class.activated]="elementsPanelVisible"
                      (click)="toggleElementsPanel()">&nbsp;
              </button>
              <button class="btn icon panel-right-icon" [class.activated]="propertiesPanelVisible"
                      (click)="togglePropertiesPanel()">&nbsp;
              </button>

              <div style="margin-left: 1rem;">
                <label>Zoom: </label>
                <select [(ngModel)]="zoom">
                  <option value="0.25">25%</option>
                  <option value=".5">50%</option>
                  <option value="0.75">75%</option>
                  <option value="1">100%</option>
                  <option value="1.5">150%</option>
                  <option value="2">200%</option>
                  <option value="4">400%</option>
                  <option value="8">800%</option>
                </select>
              </div>
            </div>

            <div class="d-flex align-items-center">
              <button class="btn icon"
                      [ngClass]="{'resize-small-icon': designer.fullscreen, 'resize-full-icon': !designer.fullscreen }"
                      (click)="designer.toggleFullScreen()">&nbsp;
              </button>
              <button class="btn icon floppy-disk-icon" (click)="onDesignerSave()">&nbsp;</button>
              &nbsp;
              <button class="btn icon remove-icon" (click)="closed.emit()">&nbsp;</button>
            </div>

          </nav>

        </ui-designer-header>

        <ui-designer-h-box>
          <ui-designer-panel *ngIf="elementsPanelVisible" [bordered]="true" [width]="'16rem'">

            <ul class="nav nav-tabs primary-tabs" role="tablist" style="padding-top: 0.75rem; background-color: #fff;">
              <li class="nav-item">
                <a class="nav-link" [ngClass]="{'active': contextView === libraryContextView}"
                   href="javascript:;" (click)="changeContextView(libraryContextView)" role="tab">Library</a>
              </li>
              <li class="nav-item">
                <a class="nav-link" [ngClass]="{'active': contextView === testContextView}"
                   href="javascript:;" (click)="changeContextView(testContextView)" role="tab">Test</a>
              </li>
            </ul>

            <section>
              <ng-template [ngIf]="contextView === libraryContextView">
                <wf-vertex-library (clicked)="onLibraryElementClick($event)"></wf-vertex-library>
              </ng-template>

              <ng-template [ngIf]="contextView === testContextView">
                <wf-designer-test [workflow]="workflow"></wf-designer-test>
              </ng-template>
            </section>

          </ui-designer-panel>

          <ui-designer-panel [bordered]="false" [flex]="1">
            <wf-graph [workflow]="workflow" [zoom]="zoom" #graph
                      (vertexSelected)="onGraphVertexSelected($event)"
                      (multipleVerticesSelected)="onGraphMultipleVerticesSelected($event)"
                      (edgeAdded)="onGraphEdgeAdded($event)"
                      (vertexMoved)="onGraphVertexMoved($event)"></wf-graph>

            <div *ngIf="verticesSelected && verticesSelected.length > 0" class="floating-actions">
              <span style="vertical-align: sub;"><strong>{{verticesSelected.length}}</strong> selected.</span>
              <span style="float: right">
                <button class="btn btn-sm btn-danger" (click)="onSelectedRemoved()">Remove</button>
                <button class="btn btn-sm btn-warning" (click)="onSelectedCloned()">Clone</button>
              </span>
            </div>

          </ui-designer-panel>

          <ui-designer-panel *ngIf="propertiesPanelVisible" [bordered]="true" [width]="'16rem'">
            <section class="separate">
              <wf-designer-vertex-properties [vertex]="selectedVertex"></wf-designer-vertex-properties>
            </section>
          </ui-designer-panel>

        </ui-designer-h-box>

      </ui-designer-v-box>
    </ui-designer>

  `,
  styles: [`
    ui-designer-header nav label {
      vertical-align: middle;
      margin: 0 0.5rem;
    }

    .btn.icon.activated {
      opacity: 0.9;
    }

    .floating-actions {
      padding: 0.75rem;
      background: #FFF;
      border: 1px solid #555;
      border-radius: 0.5rem;
      width: 20rem;
      position: absolute;
      bottom: 3rem;
      left: 50%;
      box-shadow: 0px 0px 15px -1px rgba(0, 0, 0, 0.75);
    }
  `]
})
export class WorkflowDesignerComponent extends AbstractWorkflowDesignerDispatcherObserver
  implements OnInit, OnDestroy, OnChanges {

  @Input() workflow: Workflow;
  @Input() dependencyProvider: WorkflowDependencyProvider;

  @Input() maximized = false;
  @Output() closed = new EventEmitter<any>();
  @Output() saved = new EventEmitter<IWorkflow>();

  @ViewChild('graph') wfGraph: WorkflowGraphComponent;

  readonly libraryContextView = ContextView.Library;
  readonly testContextView = ContextView.Test;

  contextView: ContextView = ContextView.Library;
  selectedVertex: WorkflowVertex;

  elementsPanelVisible = true;
  propertiesPanelVisible = false;

  verticesSelected: WorkflowVertex[] = [];

  zoom = 0.75;
  visibilityTypeText = null;

  constructor(dispatcherService: WorkflowDesignerDispatcherService, protected cdr: ChangeDetectorRef) {
    super(dispatcherService);
  }

  toggleElementsPanel() {
    this.dispatcherService.dispatchAction(this.elementsPanelVisible ?
      ActionType.ElementsPanelHideRequest : ActionType.ElementsPanelShowRequest, this);
  }

  togglePropertiesPanel() {
    this.dispatcherService.dispatchAction(this.propertiesPanelVisible ?
      ActionType.PropertiesPanelHideRequest : ActionType.PropertiesPanelShowRequest, this);
  }

  onDesignerSave() {
    const serialize = this.workflow.toJSON();

    if (serialize.dependencies && serialize.dependencies.length > 0) {
      for (const dep of serialize.dependencies) {
        if (dep.id === '') {
          // Id is blank. Do not save
          alert('Make sure you have selected a proper Sub Workflow. There is at-least one sub workflow, where nothing is selected.');
          return;
        }
      }
    }

    this.saved.emit(serialize);
  }

  changeContextView(view: ContextView) {
    this.dispatcherService.dispatchAction(view === ContextView.Library ?
      ActionType.LibraryViewRequest : (view === ContextView.Test ?
        ActionType.TestViewRequest : null), this);
  }

  onLibraryElementClick(vertexSpecification: WorkflowVertexSpecification) {
    this.dispatcherService.dispatchAction(ActionType.VertexAddRequest, this, vertexSpecification);
  }

  onGraphVertexSelected(vertex: WorkflowVertex) {
    this.dispatcherService.dispatchAction(ActionType.VertexSelected, this, vertex);
  }

  onGraphMultipleVerticesSelected(vertexList: WorkflowVertex[]) {
    this.dispatcherService.dispatchAction(ActionType.MultiVertexSelected, this, vertexList);
  }

  onGraphEdgeAdded(edge: IWorkflowEdge) {
    this.dispatcherService.dispatchAction(ActionType.EdgeAddRequest, this, edge);
  }

  onGraphVertexMoved(vertex: WorkflowVertex) {
    this.dispatcherService.dispatchAction(ActionType.VertexPropertiesUpdated, this, vertex);
  }

  onSelectedCloned() {
    const selected = this.verticesSelected || [],
      count = selected.length;

    if (count === 0 || !confirm(`Are your sure you want to clone ${count} item(s). This will create clone edges as ` +
      `well (if source and destination both are in the selection).`)) {
      return;
    }

    const record = [],
      cloneMap: IAssociativeMap<string> = {};

    for (const original of selected) {
      const clone = WorkflowVertexFactory.get({
        ...original.toJSON(),
        key: undefined,
        id: undefined,
        x: original.x + 20,
        y: original.y + 20
      }, this.dependencyProvider);

      cloneMap[original.key] = clone.key;
      record.push({original, clone});
    }

    // Add all to workflow.
    const clones = record.map(r => r.clone);

    this.wfGraph.setSelection(clones);

    clones.forEach(clone => this.addVertexToWorkflow(clone));
    const edges = [];

    for (const r of record) {
      r.original.outgoing.forEach((e: WorkflowEdge) => {

        if (!(e.toKey in cloneMap)) {
          return;
        }

        const toKey = cloneMap[e.toKey],
              fromKey = r.clone.key,
              outlet = e.outlet ? e.outlet.replace(r.original.key, fromKey) : undefined,
              key = outlet ? `${fromKey}:${outlet}->${toKey}` : `${fromKey}->${toKey}`;

        const factory = new WorkflowFactory(this.dependencyProvider);
        const specs = {
          ...e.toJSON(),
          key,
          fromKey,
          toKey,
          outlet
        };

        const edge = this.workflow.addEdge(<IGraphEdge>specs, <any>factory);
        edges.push(edge);
      });
    }

    edges.forEach(edge => this.dispatcherService.dispatchAction(ActionType.EdgeAdded, this, edge));
    this.verticesSelected = clones;
  }

  onSelectedRemoved() {
    const selected = this.verticesSelected || [],
      count = selected.length;

    if (count === 0 || !confirm(`Are your sure you want to remove ${count} item(s).`)) {
      return;
    }

    for (const vertex of selected) {
      this.removeVertex(vertex, true);
    }

    // Clear the selection
    this.wfGraph.clearSelection();
    this.verticesSelected = [];
  }

  onDispatcherEvent(action: WorkflowDesignerDispatcherAction) {
    // console.log('Action: ', action);

    switch (action.type) {
      case ActionType.ElementsPanelHideRequest:
      case ActionType.ElementsPanelShowRequest:
        this.elementsPanelVisible = action.type === ActionType.ElementsPanelShowRequest;
        break;

      case ActionType.PropertiesPanelHideRequest:
        this.propertiesPanelVisible = false;
        break;

      case ActionType.VertexSelected:
        this.selectedVertex = action.data;
        this.propertiesPanelVisible = true;
        this.verticesSelected = []; // Clear multi selection
        break;

      case ActionType.MultiVertexSelected:
        this.selectedVertex = null;
        this.propertiesPanelVisible = false;

        this.verticesSelected = action.data;
        break;

      case ActionType.PropertiesPanelShowRequest:
        this.propertiesPanelVisible = true;
        break;

      case ActionType.LibraryViewRequest:
        this.contextView = this.libraryContextView;
        break;

      case ActionType.TestViewRequest:
        this.contextView = this.testContextView;
        break;

      case ActionType.VertexAddRequest:
        this.addVertex(action.data);
        break;

      case ActionType.EdgeAddRequest:
        this.addEdge(action.data);
        break;

      case ActionType.EdgeMoveRequest:
        this.moveEdge(action.data);
        break;

      case ActionType.EdgeRemoveRequest:
        this.removeEdge(action.data);
        break;

      case ActionType.VertexRemoveRequest:
        this.removeVertex(action.data);
        break;

      case ActionType.VertexRemoved:
        if (this.selectedVertex === action.data) {
          this.selectedVertex = null;
        }
        break;

      case ActionType.VertexPropertiesUpdated:
        if (action.originator !== this) {
          this.wfGraph.vertexUpdated(action.data);
        }
        break;

      case ActionType.WorkflowTestRequest:
        this.testWorkflow(action.data);
        break;
    }
  }

  testWorkflow(data: any) {
    const output = this.wfGraph.test(data);
    console.log(output);
  }

  addVertex(vertexSpecification: WorkflowVertexSpecification) {
    const vertex = WorkflowVertexFactory.get(Object.assign({
      x: 100,
      y: 100
    }, vertexSpecification.defaultConfiguration), this.dependencyProvider);

    this.addVertexToWorkflow(vertex);
  }

  addVertexToWorkflow(vertex: WorkflowVertex) {
    this.workflow.vertices.add(vertex);
    this.dispatcherService.dispatchAction(ActionType.VertexAdded, this, vertex);
  }

  addEdge(edgeSpecification: IWorkflowEdge) {
    const factory = new WorkflowFactory(this.dependencyProvider);
    const edge = this.workflow.addEdge(<IGraphEdge>edgeSpecification, <any>factory);

    this.dispatcherService.dispatchAction(ActionType.EdgeAdded, this, edge);
  }

  moveEdge(data: { edge: WorkflowEdge, steps: number, direction: string }) {
    // Set the order
    data.edge.order = (data.edge.order || 0) + data.steps;

    const edges = data.direction === 'in' ? data.edge.toVertex.incoming : data.edge.fromVertex.outgoing;

    // Reorder TO and FROM
    edges.reorder();

    this.dispatcherService.dispatchAction(ActionType.EdgeMoved, this, data);
  }

  removeEdge(edge: WorkflowEdge) {
    this.workflow.removeEdge(edge.key);
    this.dispatcherService.dispatchAction(ActionType.EdgeRemoved, this, edge);
  }

  removeVertex(vertex: WorkflowVertex, ignoreConfirmation = false) {

    if (!ignoreConfirmation && !confirm(`Are you sure you want to remove vertex: ${vertex.name}?`)) {
      return;
    }

    this.workflow.removeVertex(vertex.key);
    this.dispatcherService.dispatchAction(ActionType.VertexRemoved, this, vertex);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.workflow) {
      const visibilityOption = WorkflowVertexLibrary.getVisibilityTypeOptions().find(o => o.value === this.workflow.visibilityType);

      if (visibilityOption) {
        this.visibilityTypeText = this.workflow.visibilityType === WorkflowVisibilityType.Both ? 'Internal and External'
          : visibilityOption.name;
      }
    }
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
  }

  ngOnInit(): void {
    super.ngOnInit();
  }
}



// WEBPACK FOOTER //
// ./src/library/designer/workflow-designer.component.ts